// ============================================================================
// Produits de lettres nommées (moteur v41, refonte lot 6.16).
// ============================================================================
// Le lexique lit une suite de lettres comme UN nom : « ab », « AB », « petit₁ » sont des
// grandeurs (une longueur AB, un prix…). Dans une écriture littérale, « 2ab » veut dire
// 2 × a × b quand a et b sont déjà des lettres de la feuille. Règle : un mot de lettres
// est découpé en ses lettres si CHACUNE est une lettre nommée de la feuille (grandeur
// déclarée, inconnue, lettre seule de l'énoncé, valeur proposée d'une affirmation) et si
// le mot lui-même n'est pas un nom déclaré. « 3ab² » = 3 × a × b², « (ab)³ » = (a × b)³.
//
// Le découpage se fait sur les TEXTES du modèle, à la lecture (ScratchModelSchema) :
// « 2ab » devient « 2a b » — deux lettres séparées par une espace, que tout lexique
// (même celui d'une app plus ancienne) lit a × b, et que la feuille affiche « 2ab ».
// Pur, sans état.
// ============================================================================

import { compoundAt, FUNCS, lex, PARMI } from './lexer';

/** les lettres d'un nom (le lexique en lit aussi ℓ, Ĉ… : jamais découpés) */
const LETTER_RE = /^[A-Za-zÀ-ÖØ-öø-ÿΑ-Ωα-ορ-ω]$/;
/** une suite de lettres (π à part : « 2πrh » → 2 × π × r × h) */
const RUN_RE = /[A-Za-zÀ-ÖØ-öø-ÿΑ-Ωα-ορ-ω]+/g;
/** ce qui, collé à un mot, en fait un autre nom : indice, prime, chapeau, barre, P_A */
const GLUE_RE = /[\u2080-\u209c′'’_]|\p{M}/u;
/** mots qui ne sont jamais des produits */
const RESERVED = new Set([
  ...Object.keys(FUNCS),
  PARMI,
  'ou',
  'et',
  'det',
  'card',
  'pgcd',
  'PGCD',
  'ppcm',
  'PPCM',
  'mod',
]);

/** plugins du noyau dont les textes sont des écritures littérales */
const LITERAL_PLUGINS = new Set(['algebra', 'formula', 'claims', 'analysis']);

type Txt = string | undefined;
interface ModelLike {
  symbols?: Record<string, unknown>;
  givens?: string[];
  admitted?: string[];
  answers?: string[];
  path?: string[];
  preds?: { line?: string }[];
  traps?: { line?: string }[];
  checks?: { line?: string }[];
  choices?: { line?: string }[];
  kernel?: {
    plugin?: string;
    goal?: { target?: string };
    start?: string;
    answer?: string;
    path?: string[];
    givens?: string[];
    domain?: string;
    hypotheses?: { of?: string }[];
    system?: { equations?: string[]; unknowns?: string[] };
    claims?: { rels?: string[]; at?: Record<string, string> }[];
    induction?: { hypothesis?: string; variable?: string };
  };
}

const single = (n: Txt): n is string => !!n && LETTER_RE.test(n);

/** Les lettres nommées d'une feuille (voir l'en-tête). */
export function namedLetters(model: ModelLike): Set<string> {
  const out = new Set<string>();
  const add = (n: Txt): void => {
    if (single(n)) out.add(n);
  };
  Object.keys(model.symbols ?? {}).forEach(add);
  const k = model.kernel;
  add(k?.goal?.target);
  (k?.hypotheses ?? []).forEach((h) => add(h.of));
  (k?.system?.unknowns ?? []).forEach(add);
  add(k?.induction?.variable);
  (k?.claims ?? []).forEach((c) => Object.keys(c.at ?? {}).forEach(add));
  // les lettres écrites seules dans l'énoncé
  const statement = [
    k?.start,
    ...(k?.givens ?? []),
    ...(k?.system?.equations ?? []),
    ...(model.givens ?? []),
  ];
  for (const t of statement) {
    if (!t) continue;
    try {
      lex(t).forEach(add);
    } catch {
      /* texte illisible : rien à apprendre */
    }
  }
  return out;
}

/** Un mot à découper en lettres ? */
function splits(word: string, known: ReadonlySet<string>, names: ReadonlySet<string>): boolean {
  if (word.length < 2 || RESERVED.has(word) || names.has(word)) return false;
  return [...word].every((c) => known.has(c));
}

/**
 * Un texte avec ses produits de lettres nommées découpés : « 2ab + ab » → « 2a b + a b ».
 * `names` : les noms déclarés (jamais découpés).
 */
export function splitNamedProducts(
  text: string,
  known: ReadonlySet<string>,
  names: ReadonlySet<string> = new Set(),
): string {
  if (!known.size) return text;
  return text.replace(RUN_RE, (word, offset: number, s: string) => {
    if (!splits(word, known, names)) return word;
    const before = s[offset - 1] ?? '';
    const after = s[offset + word.length] ?? '';
    // collé à une autre lettre (indice, chapeau…), ou nom d'une notation fonctionnelle
    // « ab(2) » : on laisse
    if (GLUE_RE.test(before) || GLUE_RE.test(after)) return word;
    if (after === '(' && compoundAt(s.slice(offset))) return word;
    return [...word].join(' ');
  });
}

/** Le modèle, ses textes avec les produits de lettres nommées découpés (voir l'en-tête). */
export function withNamedProducts<T extends ModelLike>(model: T): T {
  const k = model.kernel;
  if (!k || !LITERAL_PLUGINS.has(k.plugin ?? '')) return model;
  const known = namedLetters(model);
  if (known.size < 2) return model;
  const names = new Set(Object.keys(model.symbols ?? {}));
  let changed = false;
  const f = (t: Txt): Txt => {
    if (typeof t !== 'string') return t;
    const r = splitNamedProducts(t, known, names);
    if (r !== t) changed = true;
    return r;
  };
  const fa = (a: string[] | undefined): string[] | undefined => a?.map((t) => f(t)!);
  const fl = <L extends { line?: string }>(a: L[] | undefined): L[] | undefined =>
    a?.map((x) => (x.line === undefined ? x : { ...x, line: f(x.line)! }));
  const kernel = {
    ...k,
    start: f(k.start),
    answer: f(k.answer),
    path: fa(k.path),
    givens: fa(k.givens),
    domain: f(k.domain),
    ...(k.system ? { system: { ...k.system, equations: fa(k.system.equations) } } : {}),
    ...(k.claims ? { claims: k.claims.map((c) => ({ ...c, rels: fa(c.rels) })) } : {}),
    ...(k.induction
      ? { induction: { ...k.induction, hypothesis: f(k.induction.hypothesis) } }
      : {}),
  };
  const out = {
    ...model,
    kernel,
    givens: fa(model.givens),
    admitted: fa(model.admitted),
    answers: fa(model.answers),
    path: fa(model.path),
    preds: fl(model.preds),
    traps: fl(model.traps),
    checks: fl(model.checks),
    choices: fl(model.choices),
  };
  if (!changed) return model;
  // pas de clé `undefined` ajoutée : le modèle relu garde exactement ses clés
  const clean = (o: Record<string, unknown>, from: Record<string, unknown>): void => {
    Object.keys(o).forEach((key) => {
      if (o[key] === undefined && !(key in from)) delete o[key];
    });
  };
  clean(out as unknown as Record<string, unknown>, model as unknown as Record<string, unknown>);
  clean(kernel as unknown as Record<string, unknown>, k as unknown as Record<string, unknown>);
  return out as T;
}

/** moteur v41 : le modèle écrit-il un produit de lettres nommées (« 2ab ») ? */
export function usesNamedProducts(model: ModelLike): boolean {
  return withNamedProducts(model) !== model;
}
